import {
  type AtomKvsStorage,
  getAtomKvsStorage,
} from '@listening/frontend/atoms/kvs';
import { createIsomorphicFn } from '@tanstack/react-start';
import { getCookie } from '@tanstack/react-start/server';
import { Duration } from 'effect';
import { Cookies } from 'effect/http';
import { isSSR } from '@/env';

const keptFor = Duration.days(400);

const writeCookie = (key: string, value: string, maxAge: Duration.Input) => {
  // oxlint-disable-next-line unicorn/no-document-cookie -- a synchronous write: the next page request must carry the new value
  document.cookie = Cookies.serializeCookie(
    Cookies.makeCookieUnsafe(key, value, {
      path: '/',
      maxAge,
      sameSite: 'lax',
      secure: location.protocol === 'https:',
    }),
  );
};

// A value saved before the key moved to a cookie is copied over once.
const readCookie = createIsomorphicFn()
  .server((key: string) => getCookie(key) ?? null)
  .client((key: string) => {
    const value = Cookies.parseHeader(document.cookie)[key];
    if (value !== undefined) return value;
    const local = getAtomKvsStorage().getItem(key);
    if (local !== null) writeCookie(key, local, keptFor);
    return local;
  });

/**
 * Keeps values in cookies, which the server reads from the page request, so a
 * page it renders shows the stored value. The server never writes them.
 */
export const cookieKvsStorage: AtomKvsStorage = {
  getItem: readCookie,
  setItem: (key, value) => {
    if (!isSSR) writeCookie(key, value, keptFor);
  },
  removeItem: (key) => {
    if (!isSSR) writeCookie(key, '', Duration.zero);
  },
};
